import { useMutation, useQueryClient } from '@tanstack/react-query';

import { setDefaultExecutionImage } from '@/lib/api/generated/api';
import type { ExecutionImageView } from '@/lib/api/generated/model';

import { toExecutionImageError } from '../errors';
import { executionImageKeys } from '../queryKeys';

/**
 * Mark one catalog entry as the workspace's default deployment image
 * (FR-009). Exactly one entry holds `is_default: true` at any time — the
 * backend clears the previous default as part of the same operation, so a
 * single invalidation of the list is enough to reflect both rows changing.
 */
export function useSetDefaultExecutionImage(workspaceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (imageId: string): Promise<ExecutionImageView> => {
      const response = await setDefaultExecutionImage(workspaceId, imageId);
      if (response.status === 200) return response.data;
      throw toExecutionImageError(response.status, response.data);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: executionImageKeys.list(workspaceId),
      });
    },
  });
}
